import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@noor/ui';
import { useVendorBroadcasts, type VendorBroadcast } from '@/entities/vendor-broadcast';
import { useMarkBroadcastsViewed } from '../model/use-mark-broadcasts-viewed';
import { BroadcastCard } from './broadcast-card';

/**
 * The interruptive fresh-items popup — the old panel's `BroadcastModal`,
 * ported. It snapshots the fresh list on open and marks it viewed, so items
 * arriving (or lapsing) mid-read don't shift the dialog under the merchant.
 */
export function FreshBroadcastsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const { mutate: markViewed } = useMarkBroadcastsViewed();
  const [snapshot, setSnapshot] = useState<VendorBroadcast[] | null>(null);
  const { data: items = [] } = useVendorBroadcasts('fresh');

  // Capture-on-first-data, adjusted during render (React's endorsed pattern
  // for state derived from other state): the dialog must keep showing the set
  // it opened with even after the mark-viewed invalidation empties the live
  // fresh list. Closing resets the snapshot so the next session re-captures.
  if (open && snapshot === null && items.length > 0) {
    setSnapshot(items);
  } else if (!open && snapshot !== null) {
    setSnapshot(null);
  }

  const markedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      markedRef.current = false;
      return;
    }
    if (markedRef.current || items.length === 0) return;
    markedRef.current = true;
    markViewed(items.map((item) => item.id));
  }, [open, items, markViewed]);

  const displayItems = snapshot ?? items;

  return (
    <Dialog
      open={open && displayItems.length > 0}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        // Purely informational: a backdrop tap dismisses (the shared default
        // is FALSE — see `DialogContent`).
        closeOnInteractOutside
        className="max-h-[calc(100dvh-75px)] gap-3 overflow-y-auto sm:max-h-[calc(100dvh-180px)] sm:max-w-[784px]"
      >
        {/* The cards carry their own titles; this labels the dialog itself. */}
        <DialogTitle className="sr-only">{t('broadcasts.notifications')}</DialogTitle>
        {displayItems.map((item) => (
          <BroadcastCard key={item.id} item={item} onResponded={onClose} />
        ))}
      </DialogContent>
    </Dialog>
  );
}
